import { supabase } from '../lib/supabaseClient';
import { sendTeamInviteEmail } from './emailService';
import type { ProjectMemberRole, OperationalRole } from '../types/enterpriseProject';

// ─── Types ──────────────────────────────────────────────────────

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface Invitation {
  id: string;
  organization_id: string;
  organization_name?: string;
  email: string;
  role: string;
  project_id: string | null;
  project_role: ProjectMemberRole | null;
  operational_role: OperationalRole | null;
  department_id: string | null;
  org_role_id: string | null;
  status: InvitationStatus;
  token: string;
  invited_by: string | null;
  expires_at: string;
  accepted_at: string | null;
  accepted_by: string | null;
  created_at: string;
}

export interface CreateInvitationParams {
  organization_id: string;
  email: string;
  role?: string;
  project_id?: string;
  project_role?: ProjectMemberRole;
  operational_role?: OperationalRole;
  /** Pre-assigned department (propagated on invite accept) */
  department_id?: string | null;
  /** Pre-assigned org role from workflow_roles_v9 (propagated on invite accept) */
  org_role_id?: string | null;
  /** Sender display name — used in the invite email */
  inviterName?: string;
  /** Organization display name — used in the invite email */
  organizationName?: string;
}

// ─── Service ────────────────────────────────────────────────────

export class InvitationService {

  /** Create an invitation, persist it, and send the invite email */
  static async create(params: CreateInvitationParams): Promise<Invitation> {
    const { data, error } = await supabase
      .from('invitations')
      .insert({
        organization_id: params.organization_id,
        email: params.email.toLowerCase().trim(),
        role: params.role ?? 'member',
        project_id: params.project_id ?? null,
        project_role: params.project_role ?? null,
        operational_role: params.operational_role ?? null,
        department_id: params.department_id ?? null,
        org_role_id: params.org_role_id ?? null,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('Já existe um convite pendente para este email nesta organização');
      }
      throw error;
    }

    const invitation = data as Invitation;

    // Send invite email (fire-and-forget — do not block the UI on email delivery)
    sendTeamInviteEmail({
      toEmail: invitation.email,
      inviterName: params.inviterName ?? '',
      organizationName: params.organizationName ?? '',
      role: invitation.role,
      invitationToken: invitation.token,
    }).catch((err) => {
      console.warn('[InvitationService] Email send failed (non-blocking):', err);
    });

    return invitation;
  }

  /** List invitations for an organization */
  static async list(orgId: string, filters?: { status?: InvitationStatus; projectId?: string }): Promise<Invitation[]> {
    let q = supabase
      .from('invitations')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });

    if (filters?.status) q = q.eq('status', filters.status);
    if (filters?.projectId) q = q.eq('project_id', filters.projectId);

    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as Invitation[];
  }

  /** Revoke a pending invitation */
  static async revoke(invitationId: string): Promise<void> {
    const { error } = await supabase
      .from('invitations')
      .update({ status: 'revoked' })
      .eq('id', invitationId)
      .eq('status', 'pending');

    if (error) throw error;
  }

  /** Accept invitation via server-side function (atomic) */
  static async accept(token: string): Promise<{ success: boolean; organization_id?: string; project_id?: string; error?: string }> {
    const { data, error } = await supabase.rpc('accept_invitation', { p_token: token });

    if (error) {
      console.error('[InvitationService.accept]', error);
      return { success: false, error: error.message };
    }

    return data as { success: boolean; organization_id?: string; project_id?: string; error?: string };
  }

  /** Get invitation details by token (public-ish, for the accept page) */
  static async getByToken(token: string): Promise<Invitation | null> {
    const { data, error } = await supabase
      .from('invitations')
      .select('*')
      .eq('token', token)
      .maybeSingle();

    if (error) {
      console.error('[InvitationService.getByToken]', error);
      return null;
    }
    if (!data) return null;

    // Fetch organization name separately
    let organization_name: string | undefined;
    try {
      const { data: org } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', data.organization_id)
        .maybeSingle();
      organization_name = org?.name ?? undefined;
    } catch {
      // non-critical
    }

    return { ...data, organization_name } as Invitation;
  }

  /**
   * Resend a pending invitation by revoking it and creating a new one.
   * Returns the new invitation.
   */
  static async resend(invitationId: string): Promise<Invitation> {
    const { data: existing, error: fetchError } = await supabase
      .from('invitations')
      .select('*')
      .eq('id', invitationId)
      .single();

    if (fetchError || !existing) throw fetchError ?? new Error('Convite não encontrado');

    // Revoke old
    await InvitationService.revoke(invitationId);

    // Re-create with same params
    return InvitationService.create({
      organization_id: existing.organization_id,
      email: existing.email,
      role: existing.role,
      project_id: existing.project_id ?? undefined,
      project_role: existing.project_role ?? undefined,
      operational_role: existing.operational_role ?? undefined,
      department_id: existing.department_id ?? undefined,
      org_role_id: existing.org_role_id ?? undefined,
    });
  }

  /** Build the invite link for a given invitation */
  static buildInviteLink(token: string): string {
    const base = window.location.origin;
    // Electron uses HashRouter
    const hash = window.location.hash !== undefined;
    return hash ? `${base}/#/invite?token=${token}` : `${base}/invite?token=${token}`;
  }
}
